import { HttpErrorResponse, type HttpInterceptorFn, type HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';

import { ApiError } from '@core/api/api-error';
import { buildApiUrl, isApiUrl } from '@core/api/api-url';
import { APP_CONFIG } from '@core/app-config';
import { AuthService, isBlocked } from '@core/auth/auth-service';
import { AuthSession } from '@core/auth/auth-session';

/**
 * Adds the access token to API calls. On a 401 it refreshes once and retries the call;
 * if the refresh fails too, the session is over. The auth endpoints are left alone: the
 * login calls need no token and the refresh cookie is the refresh call's credential.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const apiBaseUrl = inject(APP_CONFIG).apiBaseUrl;
  if (!isApiUrl(apiBaseUrl, req.url) || isApiUrl(buildApiUrl(apiBaseUrl, 'auth'), req.url)) {
    return next(req);
  }

  const session = inject(AuthSession);
  const auth = inject(AuthService);

  return next(withToken(req, session.accessToken())).pipe(
    catchError((error: unknown) => {
      if (isBlocked(error)) {
        auth.endSession('blocked');
        return throwError(() => error);
      }
      if (!isUnauthorized(error)) {
        return throwError(() => error);
      }
      return auth.refresh().pipe(
        catchError((refreshError: unknown) => {
          auth.endSession(isBlocked(refreshError) ? 'blocked' : 'expired');
          return throwError(() => error);
        }),
        switchMap((token) => next(withToken(req, token))),
      );
    }),
  );
};

function withToken<T>(req: HttpRequest<T>, token: string | null): HttpRequest<T> {
  return token ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req;
}

function isUnauthorized(error: unknown): boolean {
  return (error instanceof ApiError || error instanceof HttpErrorResponse) && error.status === 401;
}
